import { createFeatureSelector, createSelector } from '@ngrx/store';
import { WireFeedTypes } from '@src/suite/common/enums/wire-feed-types';
import { ApProduct, FeedProviderState } from '@src/suite/discover/store/models/feed.provider.models';

export const selectFeedProviderState = createFeatureSelector<FeedProviderState>('feedprovider');
export const selectIsLoading = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.isLoading);
export const selectIsNotLoading = createSelector(selectFeedProviderState, (state: FeedProviderState) => !state.isLoading);
export const selectTabIndex = createSelector(selectFeedProviderState, (state: FeedProviderState) => {
    if (state.isCredentialsTabSelected) return 0;
    if (state.isFeedsTabSelected) return 1;
    return -1;
});

export const selectFeedProviderIsLoading = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.isLoading);
export const selectIsInitialDialogOpen = createSelector(selectFeedProviderState, (state: FeedProviderState) => !state.isCredentialsDialogOpen && !state.isFeedDialogOpen);
export const selectIsCredentialsDialogOpen = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.isCredentialsDialogOpen);
export const selectIsFeedDialogOpen = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.isFeedDialogOpen);
export const selectInEditMode = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.inEditMode);
export const selectIsApiKeyDisabled = createSelector(selectFeedProviderState, (state: FeedProviderState) => {
    return !(state.credentialsType !== null);
});
export const selectIsSaveButtonDisabled = createSelector(selectFeedProviderState, (state: FeedProviderState) => {
    if (state.isCredentialsDialogOpen) {
        return !(state.apiKey?.trim().length > 0 && state.credentialsType !== null && state.credentialsTitle?.trim().length > 0);
    }
    if (state.isFeedDialogOpen) {
        if (state.feedType === WireFeedTypes.FTP || state.feedType === WireFeedTypes.SFTP) {
            return true;
        }
    }
});
export const selectIsProductStepVisible = createSelector(selectFeedProviderState, (state: FeedProviderState) => {
    return state.isFeedDialogOpen && state.feedType === WireFeedTypes.AP;
});
export const selectCredentials = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.credentials);
export const selectApCredentials = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.credentials.filter(credential => credential.feedType === WireFeedTypes.AP));
export const selectTwitterCredentials = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.credentials.filter(credential => credential.feedType === WireFeedTypes.TWITTER));
export const selectCurrentCredentials = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.currentCredentials);
export const selectCredentialsTitle = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.credentialsTitle);
export const selectCredentialsType = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.credentialsType);
export const selectApiKey = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.apiKey);
export const selectCredentialsChanged = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.credentialsChanged);

export const selectFeeds = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.feeds);

export const selectIsAccessTokenEnabled = createSelector(selectFeedProviderState, (state: FeedProviderState) => !!state.feedType);
export const selectIsSelectProductsEnabled = createSelector(selectFeedProviderState, (state: FeedProviderState) => {
    if (state.feedType === WireFeedTypes.AP && !!state.apCredentialsId) {
        return true;
    }
    return false;
});

export const selectFeedTitle = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.feedTitle);
export const selectFeedType = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.feedType);
export const selectFtpProviders = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpProviders);

export const selectFtpFeed = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpFeed);
export const selectFtpProvider = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpProvider);
export const selectFtpUrl = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpUrl);
export const selectFtpUsername = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpUsername);
export const selectFtpPassword = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpPassword);
export const selectFtpDirectory = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.ftpDirectory);

export const selectApFeed = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.apFeed);
export const selectApProducts = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.apProducts);
export const selectApCredentialsId = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.apCredentialsId);
export const selectApSelectedProducts = createSelector(selectFeedProviderState, (state: FeedProviderState) => state.apSelectedProducts);

export const selectFilterApProducts = (value: string) => {
    return createSelector(selectApProducts, (products: ApProduct[]) => {
        return products.filter(product => product.name.toLowerCase().includes(value.toLowerCase()));
    });
};
export const selectHasSelectedApProducts = createSelector(selectFeedProviderState, (state: FeedProviderState) => {
    return state.apSelectedProducts?.length > 0;
});

export const selectAPFeeds = createSelector(selectFeeds, feeds => {
    return feeds.filter(f => f.type === WireFeedTypes.AP);
});
export const selectFTPFeeds = createSelector(selectFeeds, feeds => {
    return feeds.filter(f => f.type === WireFeedTypes.FTP);
});

export const selectHasAPFeeds = createSelector(selectAPFeeds, feeds => feeds?.length > 0);
export const selectHasFTPFeeds = createSelector(selectFTPFeeds, feeds => feeds?.length > 0);
export const selectHasTwitterCredentials = createSelector(selectFeedProviderState, state => state.credentials.find(cred => cred.feedType === WireFeedTypes.TWITTER));
